import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ITagDTO, ITagUsageDTO } from '@modeso/types__aumico-lib-companies-be';
import { localizationKeys } from '../../constants/localizationKeys';

/**
 * REV-22155 — the tag box of the person form and the company form (pure view).
 *
 * Shows every tag of the organization as a pill: chosen ones solid, the others dashed. A click on a
 * pill chooses / unchooses it (`selectionChange`). "Neuen Tag anlegen" + "Anlegen" asks the page to
 * create a tag (`create`); once it appears in `tags`, the box chooses it.
 *
 * Deleting ("×" on a pill) deletes the tag for the whole organization:
 *   `deleteRequest(id)` → the page loads the usage → `usage` comes back in →
 *   0 uses: `deleteConfirm(id)` right away; otherwise a warning in the box, delete only after "Löschen".
 *
 * Only the box CONTENT — the host wraps it in its own card ("TAG" header), so it looks like the
 * other boxes of that form.
 */
@Component({
    selector: 'aumico-tag-box',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './tag-box.component.html',
    styleUrls: ['./tag-box.component.scss'],
})
export class TagBoxComponent implements OnChanges {
    /** All tags of the organization. */
    @Input() tags: ITagDTO[] = [];
    @Input() selectedTagIds: string[] = [];
    /** Usage of the tag asked about, from the tags store; null while nothing is asked. */
    @Input() usage: { id: string; usage: ITagUsageDTO } | null = null;
    /**
     * Set by the page when a tag call failed. The box only uses it to drop what it was waiting for —
     * the message itself is shown by the page.
     */
    @Input() error: unknown = null;

    @Output() selectionChange = new EventEmitter<string[]>();
    @Output() create = new EventEmitter<string>();
    @Output() deleteRequest = new EventEmitter<string>();
    @Output() deleteConfirm = new EventEmitter<string>();
    @Output() deleteCancel = new EventEmitter<void>();

    readonly local = localizationKeys;

    newTagName = '';
    /** The tag whose "×" was clicked; the warning shows while it has uses. */
    pendingDeleteId: string | null = null;
    /** Name of a tag asked to be created — chosen as soon as it appears in `tags`. */
    private pendingCreateName: string | null = null;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['error'] && this.error != null) {
            // The call failed: stop waiting, otherwise the warning or the new tag would hang.
            this.pendingDeleteId = null;
            this.pendingCreateName = null;
        }
        if (changes['usage'] && this.usage && this.usage.id === this.pendingDeleteId && this.usageTotal === 0) {
            // Nobody uses the tag: no warning needed.
            this.confirmDelete();
        }
        if (changes['tags'] && this.pendingCreateName) {
            const created = this.findByName(this.pendingCreateName);
            if (created) {
                this.pendingCreateName = null;
                if (!this.isSelected(created._id)) {
                    this.selectionChange.emit([...this.selectedTagIds, created._id]);
                }
            }
        }
    }

    get pendingDeleteTag(): ITagDTO | undefined {
        return this.tags.find((tag) => tag._id === this.pendingDeleteId);
    }

    /** The warning shows once the usage of the asked tag is known and is not 0. */
    get showDeleteWarning(): boolean {
        return this.pendingDeleteId != null && this.usage?.id === this.pendingDeleteId && this.usageTotal > 0;
    }

    get usageTotal(): number {
        return this.usage ? this.usage.usage.companiesCount + this.usage.usage.personsCount : 0;
    }

    get canCreate(): boolean {
        return this.newTagName.trim().length > 0;
    }

    isSelected(id: string): boolean {
        return this.selectedTagIds.includes(id);
    }

    toggle(id: string): void {
        this.selectionChange.emit(
            this.isSelected(id) ? this.selectedTagIds.filter((tagId) => tagId !== id) : [...this.selectedTagIds, id],
        );
    }

    requestDelete(id: string): void {
        this.pendingDeleteId = id;
        this.deleteRequest.emit(id);
    }

    confirmDelete(): void {
        if (this.pendingDeleteId == null) return;
        const id = this.pendingDeleteId;
        this.pendingDeleteId = null;
        this.deleteConfirm.emit(id);
    }

    cancelDelete(): void {
        this.pendingDeleteId = null;
        this.deleteCancel.emit();
    }

    /** A name that already exists (case-insensitive, like the backend) is chosen instead of created. */
    submitNewTag(): void {
        const name = this.newTagName.trim();
        if (!name) return;
        this.newTagName = '';
        const existing = this.findByName(name);
        if (existing) {
            if (!this.isSelected(existing._id)) {
                this.selectionChange.emit([...this.selectedTagIds, existing._id]);
            }
            return;
        }
        this.pendingCreateName = name;
        this.create.emit(name);
    }

    trackById(_: number, tag: ITagDTO): string {
        return tag._id;
    }

    private findByName(name: string): ITagDTO | undefined {
        const wanted = name.trim().toLocaleLowerCase();
        return this.tags.find((tag) => tag.name.toLocaleLowerCase() === wanted);
    }
}
